import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getNicheMeta } from "../niche/nicheMeta";
import SafeImage from "../SafeImage";
import ProfileAvatarLink from "./ProfileAvatarLink";
import { activityMetaLabel } from "../../services/fanProfileService";
import { formatTimeAgo } from "../../utils/formatTimeAgo";

function nicheAccentClass(niche) {
  const key = getNicheMeta(niche).key;
  return `up-vi-niche--${key}`;
}

function ActivityRow({ item }) {
  const dropId = item.dropId || item.momentId || item.postId;
  const niche = getNicheMeta(item.niche);

  return (
    <Link to={`/drop/${dropId}`} className="up-vault-item">
      <div className={`up-vi-accent ${nicheAccentClass(item.niche)}`} />
      <div className="up-vi-thumb">
        <SafeImage src={item.thumbnailUrl} alt="" className="up-vi-thumb-img" fallbackText="" />
        <div className="up-vi-shade" />
      </div>
      <div className="up-vi-body">
        <div className="up-vi-top">
          <div className="up-vi-creator">
            {item.creatorId && (
              <ProfileAvatarLink
                userId={item.creatorId}
                isCreator
                handle={item.creatorHandle}
                src={item.creatorAvatar}
                alt={item.creatorName || item.creatorHandle || "Creator"}
                className="up-vi-cr-av-link"
                imgClassName="up-vi-cr-av"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <span className="up-vi-cr-name">@{item.creatorHandle || "creator"}</span>
            <span className={`up-vi-cr-niche ${nicheAccentClass(item.niche)}`}>{niche.label}</span>
          </div>
          {item.type === "firstview" && item.rank && (
            <span className="up-vi-badge up-vi-badge--first">#{item.rank} of 100</span>
          )}
          {item.type === "unlocked" && (
            <span className="up-vi-badge up-vi-badge--sub">Unlocked</span>
          )}
        </div>
        <div className="up-vi-title">{item.title || "Untitled drop"}</div>
        <div className="up-vi-meta">
          <span className="up-vi-type">{activityMetaLabel(item)}</span>
          {item.activityAt && (
            <span className="up-vi-date">{formatTimeAgo(item.activityAt)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function ActivityTab({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { fetchRecentActivity } = await import("../../services/fanProfileService");
        const rows = await fetchRecentActivity(userId, 24);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error("Activity tab load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return <div className="up-pc-loading">Loading activity…</div>;
  }

  if (!items.length) {
    return (
      <div className="up-pc-empty">
        <h3 className="up-pc-empty-title">No activity yet</h3>
        <p className="up-pc-empty-sub">Unlock drops from creators you follow — your activity will show up here.</p>
        <Link to="/gallery" className="up-pc-empty-cta">Browse drops</Link>
      </div>
    );
  }

  return (
    <div className="up-activity-wrap">
      <div className="up-section-head">
        <div>
          <h2 className="up-sh-title">Recent <em>activity</em></h2>
          <span className="up-sh-hand">{items.length} recent unlocks</span>
        </div>
      </div>
      <div className="up-vault-list">
        {items.map((item) => (
          <ActivityRow key={`${item.type}-${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
}
